// src/supabaseClient.js
import { Capacitor } from "@capacitor/core";
import { createClient } from "@supabase/supabase-js";

export const supabaseProjectUrl = process.env.REACT_APP_SUPABASE_URL;
export const supabaseBuildInfo = {
  buildNumber: process.env.REACT_APP_BUILD_NUMBER || "unknown",
  commitSha: process.env.REACT_APP_COMMIT_SHA || "unknown",
};
const supabaseUrl = supabaseProjectUrl;
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

export const missingSupabaseEnv = [];
if (!supabaseUrl) {
  missingSupabaseEnv.push("REACT_APP_SUPABASE_URL");
}
if (!supabaseAnonKey) {
  missingSupabaseEnv.push("REACT_APP_SUPABASE_ANON_KEY");
}

if (missingSupabaseEnv.length > 0) {
  console.warn(
    `Supabase env vars are missing: ${missingSupabaseEnv.join(", ")}`
  );
}

const SUPABASE_SESSION_KEY = "flagiq:supabase:session";

let shouldPersistSupabaseSession = true;

export const setSupabaseSessionPersistence = (enabled) => {
  shouldPersistSupabaseSession = Boolean(enabled);
  if (!shouldPersistSupabaseSession) {
    clearSupabaseSession();
  }
};

const getSecureStorage = () => {
  const plugin = Capacitor?.Plugins?.SecureStoragePlugin;
  if (plugin?.get && plugin?.set) {
    return plugin;
  }
  return null;
};

const getCapacitorStorage = () => {
  if (!Capacitor?.Plugins) return null;
  return Capacitor.Plugins.Preferences || Capacitor.Plugins.Storage || null;
};

const storageAdapter = {
  getItem: async (key) => {
    const storageKey = key || SUPABASE_SESSION_KEY;
    const secureStorage = getSecureStorage();
    if (secureStorage?.get) {
      try {
        const result = await secureStorage.get({ key: storageKey });
        if (typeof result?.value === "string") return result.value;
      } catch (e) {}
    }
    const plugin = getCapacitorStorage();
    if (plugin?.get) {
      try {
        const result = await plugin.get({ key: storageKey });
        if (typeof result?.value === "string") return result.value;
      } catch (e) {}
    }
    try {
      return localStorage.getItem(storageKey);
    } catch (e) {
      return null;
    }
  },
  setItem: async (key, value) => {
    if (!shouldPersistSupabaseSession) {
      return;
    }
    const storageKey = key || SUPABASE_SESSION_KEY;
    const secureStorage = getSecureStorage();
    if (secureStorage?.set) {
      try {
        await secureStorage.set({ key: storageKey, value: String(value ?? "") });
        return;
      } catch (e) {}
    }
    const plugin = getCapacitorStorage();
    if (plugin?.set) {
      try {
        await plugin.set({ key: storageKey, value: String(value ?? "") });
        return;
      } catch (e) {}
    }
    try {
      localStorage.setItem(storageKey, String(value ?? ""));
    } catch (e) {}
  },
  removeItem: async (key) => {
    const storageKey = key || SUPABASE_SESSION_KEY;
    const secureStorage = getSecureStorage();
    if (secureStorage?.remove) {
      try {
        await secureStorage.remove({ key: storageKey });
        return;
      } catch (e) {}
    }
    const plugin = getCapacitorStorage();
    if (plugin?.remove) {
      try {
        await plugin.remove({ key: storageKey });
        return;
      } catch (e) {}
    }
    try {
      localStorage.removeItem(storageKey);
    } catch (e) {}
  },
};

export const supabase =
  missingSupabaseEnv.length === 0
    ? createClient(supabaseUrl, supabaseAnonKey, {
        auth: {
          persistSession: true,
          autoRefreshToken: true,
          detectSessionInUrl: false,
          storageKey: SUPABASE_SESSION_KEY,
          storage: storageAdapter,
        },
      })
    : null;

export async function restoreSupabaseSession() {
  if (!supabase) return null;
  const raw = await storageAdapter.getItem(SUPABASE_SESSION_KEY);
  if (!raw) return null;
  try {
    const session = JSON.parse(raw);
    if (!session?.access_token || !session?.refresh_token) return null;
    const result = await supabase.auth.setSession({
      access_token: session.access_token,
      refresh_token: session.refresh_token,
    });
    if (result?.error) {
      await storageAdapter.removeItem(SUPABASE_SESSION_KEY);
      return null;
    }
    return result?.data?.session || null;
  } catch (e) {}
  return null;
}

export function subscribeToSupabaseAuth(onSession) {
  if (!supabase) return () => {};
  const { data } = supabase.auth.onAuthStateChange(
    async (event, session) => {
      if (session) {
        const payload = {
          access_token: session.access_token,
          refresh_token: session.refresh_token,
          expires_at: session.expires_at,
        };
        await storageAdapter.setItem(
          SUPABASE_SESSION_KEY,
          JSON.stringify(payload)
        );
      } else {
        await storageAdapter.removeItem(SUPABASE_SESSION_KEY);
      }
      if (onSession) {
        onSession(event, session);
      }
    }
  );
  return () => {
    data?.subscription?.unsubscribe();
  };
}

export async function clearSupabaseSession() {
  await storageAdapter.removeItem(SUPABASE_SESSION_KEY);
}
